/*
 * Created on 01.03.2017.
 *
 * Copyright(c) 1995 - 2017
 * T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Inject, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { AsyncValidator, ControlValueAccessor, NG_ASYNC_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { Observable, Subject, Subscription, forkJoin, of } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { HttpActiveRequestService, HttpProgressInfo } from '../bootstrap/http-active-request.service';
import { LanguageService } from '../i18n/language.service';
import { LoggingService } from '../utils/logging.service';
import { IUploadFileSizeConfigService } from '../utils/upload-file-size-config-service';
import { Attachment } from './attachment';
import { FILE_UPLOAD_MIME_TYPES_MAPPING } from './file-upload-mime-types.provider';
import { I18N } from './file-upload.component.i18n';
import { FileUploadService } from './file-upload.service';
import { FileUtils } from './file-utils';

/**
 * Komponente zum Hochladen von Dateien in Verbindung mit unserer "Attachment-Strategie".
 * Das heißt der Attachment-Service läuft im Backend in Verbindung mit Controllern für
 * Upload, Download und Statusabfrage nach festem Schema. Siehe {@link FileUploadComponent#url}
 *
 * Die Komponente kann auch genutzt werden um initial vorhandene Attachments
 * zu sehen und zu löschen, was z.B. bei dem Editieren eines Formulars geschieht,
 * wenn bereits einmal Daten hochgeladen wurden.
 *
 * @author Valentin Kennke (vake@mms-dresden.de)(valentin.kennke@t-systems.com)
 */
@Component({
    selector: 'common-file-upload',
    templateUrl: 'file-upload.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FileUploadComponent),
            multi: true
        },
        {
            provide: NG_ASYNC_VALIDATORS,
            useExisting: forwardRef(() => FileUploadComponent),
            multi: true
        },
        FileUploadService
    ]
})
export class FileUploadComponent implements ControlValueAccessor, AsyncValidator {

    private static readonly UNCHECKED: string = 'UNCHECKED';
    private static readonly NO_VIRUS: string = 'NO_VIRUS';
    private static readonly ERROR: string = 'ERROR';
    private static readonly MIME_TYPE_REGEX = /,\s/g;

    /**
     * Basis-URL für Upload, Download & Statusüberprüfung.
     * Der Upload erfolgt per POST an <code>$url$/</code>.
     * Der Download per GET an <code>$url$/$uuid$</code>.
     * Der Statuscheck per GET an <code>$url$/$uuid$/state</code>.
     */
    @Input() url: string;
    /**
     * Mime-Types die zum Upload akzeptiert werden (z.B. ['application/pdf', 'image/png']).
     * Groß- und Kleinschreibung ist egal. Wird kein Wert angegeben wird alles akzeptiert.
     * Arbeitet zusammen mit {@link FILE_UPLOAD_MIME_TYPES_MAPPING}.
     */
    @Input() mimeTypes: string[] = [];
    /**
     * Maximale Dateigröße in MB.
     */
    @Input() maxFileSize: number = -1;  // Dateigröße in MB
    /**
     * Das Überschrift über dem Uploadfelder.
     */
    @Input() label: string = '';
    /**
     * Gibt an, dass mindestens eine Datei hochgeladen werden muss. Standardwert ist false. Die Bedingung ist erfüllt,
     * wenn mindest eine Datei hochgeladen UND erfolreich auf Virenfreiheit geprüft wurde.
     */
    @Input() mandatory: boolean = false;
    /**
     * Gibt an ob die Felder read-only sind. Falls ja, werden die Löschbuttons disabled und das Uploadfeld nicht
     * mehr angezeigt.
     */
    @Input() uploadDisabled: boolean = false;
    /**
     * Maximale Anzahl an erlaubten Dateien. Default ist -1, d.h. unendlich viele Dateien sind erlaubt.
     */
    @Input() maxFileNumber: number = -1;
    /**
     * Gibt Metadaten über den letzten Upload zurück.
     */
    @Output() lastUploadedMetadata: EventEmitter<Attachment> = new EventEmitter<Attachment>();
    /**
     * Event emitted on successful download.
     */
    @Output() attachmentDownloaded: EventEmitter<Attachment> = new EventEmitter<Attachment>();
    @ViewChild('fileInput') input: ElementRef;

    /**
     * Hier sind alle IDs drin, die für den Upload von Dateien aus dieser Instanz der
     * Komponente genutzt wurden. Somit können wir die IDs aus {@link HttpActiveRequestService#change$}
     * herausfiltern, die für diese Instanz der Komponente wichtig sind.
     */
    allUploadIds: string[] = [];

    errorMessages: string[] = [];
    uploadedAttachments: Attachment[] = [];
    i18n: any;
    progress: number = 0;
    uploadingAttachments: File[] = [];
    accept: string;
    allowedFileEndings: string = '';

    private progresses: Map<string, number> = new Map<string, number>();
    private checkedAttachmentsUuids: Set<string> = new Set<string>();
    private uncheckedAttachmentsUuids: Set<string> = new Set<string>();
    private propagateChange: (attachmentUuids: string[]) => void;
    private readonly languageSubscription: Subscription;
    private loadId = -1;
    private isUploading: boolean = false;
    private ngUnsubscribe$: Subject<void> = new Subject<void>();
    private logger: LoggingService = LoggingService.getInstance();
    private attachmentUuidToFileMap: Map<string, string> = new Map<string, string>();
    private numberOfUploads: number = 0;
    private numberOfSuccessfulUploads: number = 0;

    constructor(
        @Inject(FILE_UPLOAD_MIME_TYPES_MAPPING) private mimeTypesMapping: { [index: string]: string },
        private languageService: LanguageService,
        private fileUploadService: FileUploadService,
        private httpActiveRequestService: HttpActiveRequestService,
        private filesizeCfgService: IUploadFileSizeConfigService
    ) {
        this.languageSubscription = languageService.registerForLanguageChanged(this, 'i18n', I18N);
    }

    /**
     * Um den Dateiauswahl-Dialog zu öffnen muss ein <input type="file" .../> angeklickt werden. Daher wird der Klick auf das Icon auf
     * ein verstecktes <input type="file" .../> "weitergeleitet".
     */
    click() {
        this.input.nativeElement.click();
    }

    ngOnInit() {
        this.subscribeHttpProgress();
        this.buildAcceptString();
        this.checkUncheckedAttachmentUuids().subscribe();
        this.allowedFileEndings = '.' + this.accept.replace(FileUploadComponent.MIME_TYPE_REGEX, ', .');
        this.setUpSizeLimit();
    }

    /**
     * Lädt die übergebene Datei hoch und startet im Anschluss die Überprüfung des Status der Datei. Falls die Datei eine nicht erlaubte
     * Dateiendung hat, zu groß ist oder die maximale Anzahl an erlaubten Dateien bereits erreicht wurde wird kein Upload durchgeführt.
     *
     * @param files die Dateien die hochgeladen werden soll
     */
    public upload(files: File[]) {
        this.logger.debug('FileUploadComponent#upload: ' + files.map(f => f.name).reduce((a, b) => a + ', ' + b));
        if (files && files.length > 0) {
            this.logger.debug('FileUploadComponent#upload: clear first');
            this.errorMessages = []; // clear first.
            if (this.maxFileNumber > -1 && (this.uploadedAttachments.length + files.length) > this.maxFileNumber) {
                this.errorMessages.push(this.i18n.tooMuchFiles + this.maxFileNumber);
            } else {
                this.uploadingAttachments = [];
                this.numberOfUploads = files.length;
                this.numberOfSuccessfulUploads = 0;
                files.forEach(file => void this.uploadFile(file));
            }
        }
    }

    public formatBytes(size: number) {
        return FileUtils.formatBytes(size);
    }

    public download(attachment: Attachment) {
        this.fileUploadService.download(attachment.uuid, this.url).subscribe(() => {
            this.attachmentDownloaded.emit(attachment);
        });
    }

    /**
     * Beim Löschen wird die Datei aus der Liste der hochgeladenen Dateien (=== uploadedAttachments) entfernt, d.h. auch nicht mehr in
     * der UI angezeigt und aus der Liste der geprüften Dateien (=== checkedAttachmentsUuids) entfernt falls vorhanden, d.h. aus dem per
     * [(ngModel)] gebundenen Ergebnis entfernt.
     *
     * @param attachment das Attachment das entfernt wird.
     */
    public delete(attachment: Attachment) {
        const uploadedIndex = this.uploadedAttachments.findIndex(uploadedAttachment => uploadedAttachment.uuid === attachment.uuid);
        if (uploadedIndex >= 0) {
            this.uploadedAttachments.splice(uploadedIndex, 1);
        }
        this.checkedAttachmentsUuids.delete(attachment.uuid);
        this.propagateChange(Array.from(this.checkedAttachmentsUuids.values()));
        this.lastUploadedMetadata.emit(null);
    }

    /**
     * Gibt an ob das Upload-Feld angezeigt werden soll. Das ist der Fall sofern keine maximale Anzahl an Dateien spezifiziert ist
     * oder die maximale Anzahl an Dateien noch nicht erreicht wurde.
     */
    public showUpload() {
        return this.maxFileNumber === -1 || !this.checkedAttachmentsUuids || this.checkedAttachmentsUuids.size < this.maxFileNumber;
    }

    ngOnDestroy() {
        if (this.languageSubscription) this.languageSubscription.unsubscribe();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    /**
     * Schreibt einen Wert in das Formular.
     *
     * @param attachmentUuids Die UUIDs der Attachments. Any nur um Legacy-Code nicht zu brechen
     */
    writeValue(attachmentUuids: string[]): void {
        if (!attachmentUuids?.length) {
            // wenn null oder leeres array
            return;
        }

        attachmentUuids.forEach(e => this.uncheckedAttachmentsUuids.add(e));
        this.checkUncheckedAttachmentUuids(true).subscribe()
    }

    registerOnChange(fn: (attachmentUuids: string[]) => void): void {
        this.propagateChange = fn;
    }

    registerOnTouched(_: any): void {
    }

    setDisabledState(isDisabled: boolean): void {
        this.uploadDisabled = isDisabled;
    }

    /**
     * Liefert einen Fehler wenn:
     *  - gerade eine Datei hochgeladen oder geprüft wird
     *  - der FileUpload ein Pflichtfeld ist und keine hochgeladene und virenfreie Datei vorhanden ist
     *  - mind. eine Dateie hochgeladen wurde die einen Virus hat
     *
     * @param c die FormControl
     * @returns null falls kein Fehler auftritt, sonst den Fehler
     */
    validate(c: UntypedFormControl): any {
        return this.checkUncheckedAttachmentUuids(true).pipe(map(() => this.createError(c)));
    }

    createError(c: UntypedFormControl) {
        if (this.isUploading) {
            return {
                error: {
                    uploading: c.value
                }
            };
        }
        if (this.mandatory && this.checkedAttachmentsUuids.size < 1) {
            return {
                error: {
                    required: c.value
                }
            };
        }
        if (this.uploadedAttachments.every(attachment => attachment.state === FileUploadComponent.NO_VIRUS)) {
            return null;
        }
        return {
            error: {
                virus: c.value
            }
        };
    }

    getHash(file: File): string {
        return file.name + '#' + file.lastModified + FileUploadService.generateId();
    }

    private async uploadFile(file: File) {
        if (!this.fileExtensionAllowed(file.name)) {
            this.errorMessages.push(this.i18n.contentTypeNotSupported + file.name);
            return;
        }
        if (file.size > this.maxFileSize * 1048576) {
            this.errorMessages.push(this.i18n.fileTooLarge + file.name);
            return;
        }
        if (this.maxFileNumber !== -1 && this.uploadedAttachments.length >= this.maxFileNumber) {
            return;
        }
        this.uploadingAttachments.push(file);
        const id = this.getHash(file);
        if (!this.progresses.get(id)) {
            this.setProgress(id, 1);
        }
        this.allUploadIds.push(id);
        this.fileUploadService.uploadFile(file, this.url, id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (att: Attachment) => {
                    this.attachmentUuidToFileMap.set(att.uuid, id);
                    this.isUploading = true;
                    this.propagateChange(Array.from(this.checkedAttachmentsUuids.values()));
                    this.onItemUpload(att);
                    this.numberOfSuccessfulUploads += 1;
                    if (this.numberOfUploads <= this.numberOfSuccessfulUploads) {
                        this.logger.debug('FileUploadComponent#uploadFile#subscription: clear errorMessages: '
                            + this.numberOfUploads + ', ' + this.numberOfSuccessfulUploads);
                        this.errorMessages = [];
                    }

                },
                error: (error: HttpErrorResponse) => {
                    this.uploadingAttachments.splice(this.uploadingAttachments.findIndex(att => att.name === file.name), 1);
                    if (error.status === 415) {
                        this.errorMessages.push(this.i18n.contentTypeNotSupported + file.name);
                    } else if (error.status === 413) {
                        this.errorMessages.push(this.i18n.fileTooLarge + file.name);
                    } else {
                        this.logger.debug('FileUploadComponent#uploadFile#subscription: error, ' + JSON.stringify(error));
                        this.errorMessages.push(this.i18n.uploadError + file.name);
                    }
                }
            });
    }

    /**
     * Wird aufgerufen wenn die Datei erfolgreich hochgeladen wurde. Hier wird in einem Intervall von 1 Sekunde der Virenscan-Status
     * der Datei abgefragt und entsprechend im Modell aktualisiert.
     *
     * @param attachment die Datei die hochgeladen wurde
     */
    private onItemUpload(attachment: Attachment) {
        const interval = setInterval(() => {
            this.fileUploadService.checkState(attachment.uuid, this.url)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: (response: Attachment) => {
                        const id = this.attachmentUuidToFileMap.get(response.uuid);
                        this.setProgress(id, Math.min(this.progresses.get(id) + 5, 100));
                        attachment.state = response.state;
                        if (attachment.state !== FileUploadComponent.UNCHECKED) {
                            this.isUploading = false;
                            this.setProgress(id, 100);
                            this.uploadingAttachments.splice(this.uploadingAttachments.findIndex(att => att.name === response.name), 1);
                            if (attachment.state !== FileUploadComponent.ERROR
                                && (this.maxFileNumber === -1 || this.uploadedAttachments.length < this.maxFileNumber)) {
                                this.addToUploadedAttachmentsOnce(attachment);
                            } else {
                                this.logger.debug('FileUploadComponent#onItemUpload#subscription: anderes: '
                                    + JSON.stringify(attachment.state));
                                this.logger.debug('FileUploadComponent#onItemUpload#subscription: this.uploadedAttachments.length '
                                    + this.uploadedAttachments.length);
                                this.logger.debug('FileUploadComponent#onItemUpload#subscription: this.maxFileNumber '
                                    + this.maxFileNumber);
                                if (!this.uploadedAttachments.filter(ul => ul.uuid === response.uuid).length) {
                                    this.errorMessages.push(this.i18n.uploadError + attachment.name);
                                }
                            }
                            this.loadId = -1;
                            clearInterval(interval);
                            if (attachment.state === FileUploadComponent.NO_VIRUS
                                && (this.maxFileNumber === -1 || this.checkedAttachmentsUuids.size < this.maxFileNumber)) {
                                this.checkedAttachmentsUuids.add(attachment.uuid);
                                this.lastUploadedMetadata.emit(attachment);
                            }
                        }
                        this.propagateChange(Array.from(this.checkedAttachmentsUuids.values()));
                    },
                    error: (err) => {
                        this.propagateChange(Array.from(this.checkedAttachmentsUuids.values()));
                        this.logger.debug('FileUploadComponent#onItemUpload#subscription, ' + JSON.stringify(err));
                        this.errorMessages.push(this.i18n.uploadError + attachment.name);
                        clearInterval(interval);
                    }
                });
        }, 1000);
    }

    private setProgress(id: string, value: number, accumulate: boolean = false): void {
        if (value === 0) {
            this.progresses.delete(id);
        } else {
            const addVal = accumulate && this.progresses.get(id) ? this.progresses.get(id) : 0;
            this.progresses.set(id, addVal + value);
        }
        const average = Array.from(this.progresses.values()).reduce((acc, value, index) => acc + (value - acc) / (index + 1), 0);
        if (average === 100) {
            this.progresses.clear();
        }
        this.progress = Array.from(this.progresses.values()).reduce((acc, value, index) => acc + (value - acc) / (index + 1), 0);
    }

    private fileExtensionAllowed(fileName: string): boolean {
        return this.mimeTypes.length === 0
            || new RegExp('(' + this.accept.replace(/\s*,\s*/g, '|')
                .replace(/\./g, '\\.') + ')$', 'i').test(fileName.toLowerCase());
    }

    private subscribeHttpProgress() {
        // Das Hochladen der Datei lässt den Fortschrittsbalken bis auf 50% anwachsen, der Rest wird durch die Virenprüfung gefüllt
        this.httpActiveRequestService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((progressInfo: HttpProgressInfo) => {
                const uploads = Array.from(progressInfo.downloadsInProgress.entries())
                    .filter(([_, info]) => !info.download)
                    .filter(([id, _]) => this.allUploadIds.includes(id));
                if (uploads.length || this.uncheckedAttachmentsUuids.size > 0) {
                    uploads.forEach(([_, info]) => {
                        this.setProgress(info.id, info.loaded * 0.9);
                        this.logger.debug('FileUploadComponent#subscribeHttpProgress#subscription: set progress for '
                            + info.id + ' to ' + info.loaded * 0.9);
                    });
                }
            });
    }

    /**
     * Baut den accept-String, der unter der Komponente steht und wie folgt aussieht:
     * "Es sind nur Dateien in folgendem Format erlaubt: png, jpg, jpeg, jpe, gif, tiff, tif, pdf"
     */
    private buildAcceptString() {
        this.accept = this.mimeTypes
            // "application/pdf" => "pdf"
            .map(type => type.replace(/[a-z]+\//g, ''))
            // wir kriegen mimeTypeMappings rein, was eine Map von Mime-Type auf Dateiendungen ist
            // bspw: "jpeg: 'jpg,jpeg,jpe'" -> wir ersetzen hier den Wert von jpeg durch 'jpg,jpeg,jpe' wenn vorhanden
            .map(type => {
                // mime type mappings sind als optionale Hilfe zu verstehen und keine Pflicht
                const types = this.mimeTypesMapping[type] || type;
                this.logger.debug(
                    `FileUploadComponent#buildAcceptString: MIME-Type '${type}' => Dateitypen: ${JSON.stringify(types)}`
                );
                return types;
            })
            // filter null values after mapping
            .filter(type => !!type)
            // nun das gerade erstellte Mapping wieder in einzelne Elemente aufsplitten
            .map(type => type.split(','))
            // [[1,2], [3,4]] => [1,2,3,4]
            .flatMap(types => types)
            // alphabetically order
            .sort((type1, type2) => type1.localeCompare(type2))
            // allow only unique values
            .filter((value, index, self) => self.indexOf(value) === index)
            .reduce((type1, type2) => `${type1}, ${type2}`, '')
    }

    /**
     * Ruft die Attachment-Metadaten der attachmentUuids (uncheckedAttachmentsUuids) über die URL ab.
     * Nach der Statusüberprüfung mit state == NO_VIRUS, werden die Attachments in die Liste der
     * geprüften und hochgeladenen Dateien (checkedAttachmentsUuids & uploadedAttachments) hinzugefügt und
     * über lastUploadedMetadata emittiert.
     *
     * @param isFormWrite gibt an, ob die Funktion über {@link #writeValue} aufgerufen wurde
     *                    oder nicht.
     */
    private checkUncheckedAttachmentUuids(isFormWrite: boolean = false): Observable<any> {
        // hole bereits vorhandene Attachments, prüfe den Status und füge virenfreie Attachments hinzu
        const subscriptions: Observable<Attachment>[] = [];
        if (this.uncheckedAttachmentsUuids && this.url) {
            this.uncheckedAttachmentsUuids.forEach(uuid => subscriptions.push(this.fileUploadService.checkState(uuid, this.url)));
            if (subscriptions.length > 0 ) {
                return forkJoin(subscriptions)
                    .pipe(takeUntil(this.ngUnsubscribe$), map((attachments: Attachment[]) => {
                        attachments.forEach(attachment => {
                            if (attachment.state === FileUploadComponent.NO_VIRUS) {
                                this.checkedAttachmentsUuids.add(attachment.uuid);
                                this.addToUploadedAttachmentsOnce(attachment);
                                this.lastUploadedMetadata.emit(attachment);
                                this.uncheckedAttachmentsUuids.delete(attachment.uuid);
                            }
                        });
                        // nicht hier, da der Subscribeblock aktiv ist nach dem das Formular resettet wurde (=[])
                        // this.logger.debug('FileUploadComponent#checkUncheckedAttachmentUuids: uncheckedAttachmentsUuids:');
                        // this.logger.debug(this.uncheckedAttachmentsUuids);
                        this.logger.debug('FileUploadComponent#checkUncheckedAttachmentUuids: checkedAttachmentsUuids:');
                        this.logger.debug(this.checkedAttachmentsUuids);

                        // das ist alles nicht notwendig, da wir bei einer invaliden uuid gar nicht so weit kommen (404)
                        /* this.uncheckedAttachmentsUuids.forEach(uuid => {
                            if (!this.checkedAttachmentsUuids.includes(uuid)) {
                                this.logger.warn(`FileUploadComponent#checkUncheckedAttachmentUuids: uuid ${uuid} wurde
                                nicht auf Server gefunden!`);
                            }
                        });*/

                        if (!isFormWrite) {
                            // wenn wir aus dem Form Werte reinschreiben soll das Formular nicht auf dirty gesetzt werden
                            // da wollen wir nur die Daten fetchen ohne sie nach außen erneut zu propagieren
                            this.propagateChange(Array.from(this.checkedAttachmentsUuids));
                        }
                        this.logger.debug('FileUploadComponent#checkUncheckedAttachmentUuids: uncheckedAttachmentsUuids:');
                        this.logger.debug(this.uncheckedAttachmentsUuids);
                        this.uncheckedAttachmentsUuids.clear();
                    }));
            } else {
                // gib ein nicht leeres Observable zurück damit eventuelle Stream-Operationen durch den Aufrufer ausgeführt werden können.
                return of({});
            }
        }
    }

    /**
     * Setzt die maximale Größe für Dateiuploads. Dabei werden drei Werte herangezogen:
     * 1) Input-Property (numerisch in MB) mit höchster Priorität
     * 2) ein ConfigService, der injected wird
     * 3) ein im Code hinterlegter Default-Wert.
     */
    private setUpSizeLimit(): void {
        if (this.maxFileSize > 0) {
            // dann wurde der Wert explizit von außen gesetzt
            this.logger.debug(`FileUploadComponent#setUpSizeLimit: maxFileSize als Input-Property übergeben: ${this.maxFileSize}MB`);
        } else {
            // Wert aus Service holen (da wir ein BehaviorSubject benutzen kriegen wir in jedem Fall den aktuellen Wert)
            this.logger.debug(`FileUploadComponent#setUpSizeLimit: kein Wert für "maxFileSize"-Input-Property, aber `
                + `IUploadFileSizeConfigService-Instanz vorhanden!`);
            this.filesizeCfgService.getMaxFileSizeObservable()
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(maxFileSize => {
                    this.maxFileSize = maxFileSize;
                    this.logger.debug(`FileUploadComponent#setUpSizeLimit: neuer Wert für maximale Dateigröße ist `
                        + `${this.filesizeCfgService.currentFileSize}MB`);
                });
        }
    }

    /**
     * Stellt sicher, dass auch bei Nebenläufigkeit,
     * keine Attachments mit derselben UUID in uploadedAttachments geschrieben werden.
     * @param attachment
     * @private
     */
    private addToUploadedAttachmentsOnce(attachment: Attachment) {
        // UUID bereits vorhanden?
        if(this.uploadedAttachments
            .map((uploadedAttachment: Attachment) => uploadedAttachment.uuid)
            .includes(attachment.uuid)){
            // Attachment bereits geladen wird ersetzt
            this.uploadedAttachments = this.uploadedAttachments.map(uploadedAttachment =>
                uploadedAttachment.uuid !== attachment.uuid ? uploadedAttachment : attachment
            )
        } else {
            // Neues Attachment wird hinzugefügt
            this.uploadedAttachments.push(attachment);
        }
    }
}
